import React, { useState, useEffect } from "react";
import { useFormik, FieldArray, FormikProvider } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import {
    globalLoader,
    handleServerValidations,
} from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { Add } from "../../services/productAttributeValue.services";
import { List as ListAttributes } from "../../services/productAttribute.services";

const defaultValItem = {
    value: "",
    view_type: "row",
    image: null,
    preview: null,
};

const AddAttributeValue = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [attributeList, setAttributeList] = useState([]);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Attribute Values", url: "/admin/product-attribute-value/list/1" },
        { title: t("link_add"), url: "" },
    ];

    useEffect(() => {
        const params = new FormData();
        params.append("search", JSON.stringify({ status: 1 }));
        ListAttributes(params)
            .then((res) => {
                if (res && res.success) {
                    setAttributeList(res.data.list || []);
                }
            })
            .catch((err) => {
                console.log("Error fetching attributes:", err);
            });
    }, []);

    const validationSchema = Yup.object().shape({
        attribute_id: Yup.string().required("Attribute is required"),
        values: Yup.array()
            .of(
                Yup.object().shape({
                    value: Yup.string()
                        .trim()
                        .required("Value is required")
                        .max(255, "Value cannot exceed 255 characters"),
                    view_type: Yup.string().required("View type is required"),
                })
            )
            .min(1, "At least one value is required"),
    });

    const formik = useFormik({
        initialValues: {
            attribute_id: "",
            values: [{ ...defaultValItem }],
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            globalLoader(true);
            setSubmitted(true);

            const formData = new FormData();
            formData.append("attribute_id", values.attribute_id);

            const cleanedValues = values.values.map((item, idx) => {
                if (item.image) {
                    formData.append(`image_${idx}`, item.image);
                }
                return {
                    value: item.value,
                    view_type: item.view_type || "row",
                };
            });

            formData.append("values", JSON.stringify(cleanedValues));

            Add(formData)
                .then((response) => {
                    if (response && response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message || "Success",
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setTimeout(() => {
                            navigate(`/admin/product-attribute-value/list/1`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: response ? handleServerValidations(response) : "Something went wrong",
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setSubmitted(false);
                    }
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    globalLoader(false);
                    setSubmitted(false);
                });
        },
    });

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <h6 className="main-content-label mb-3">Add Attribute Values</h6>
                        <FormikProvider value={formik}>
                            <form onSubmit={formik.handleSubmit}>
                                <div className="row row-sm">
                                    {/* Attribute Dropdown */}
                                    <div className="col-lg-12 form-group">
                                        <label htmlFor="attribute_id" className="text-left d-flex">
                                            Select Attribute :<span className="requirestar">*</span>
                                        </label>
                                        <select
                                            name="attribute_id"
                                            id="attribute_id"
                                            className="form-control"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.attribute_id}
                                        >
                                            <option value="">-- Select Attribute --</option>
                                            {attributeList.map((attr) => (
                                                <option key={attr.id} value={attr.id}>
                                                    {attr.title}
                                                </option>
                                            ))}
                                        </select>
                                        {formik.errors.attribute_id && formik.touched.attribute_id && (
                                            <span className="text-danger d-flex text-left">
                                                {formik.errors.attribute_id}
                                            </span>
                                        )}
                                    </div>

                                    {/* Dynamic Values */}
                                    <div className="col-lg-12 form-group">
                                        <label className="text-left d-flex fw-bold">Values :</label>
                                        <FieldArray
                                            name="values"
                                            render={(arrayHelpers) => (
                                                <div>
                                                    {formik.values.values.map((val, index) => (
                                                        <div key={index} className="row row-sm mb-3 align-items-center bg-light p-2 rounded border">
                                                            {/* Value Field */}
                                                            <div className="col-lg-4">
                                                                <label className="small text-muted mb-1 d-block text-left">Value *</label>
                                                                <input
                                                                    name={`values.${index}.value`}
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Enter value"
                                                                    value={val.value}
                                                                    onChange={formik.handleChange}
                                                                    onBlur={formik.handleBlur}
                                                                />
                                                                {formik.errors.values?.[index]?.value && formik.touched.values?.[index]?.value && (
                                                                    <span className="text-danger small d-flex text-left">
                                                                        {formik.errors.values[index].value}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* View Type Dropdown */}
                                                            <div className="col-lg-3">
                                                                <label className="small text-muted mb-1 d-block text-left">View Type *</label>
                                                                <select
                                                                    name={`values.${index}.view_type`}
                                                                    className="form-control"
                                                                    value={val.view_type}
                                                                    onChange={formik.handleChange}
                                                                    onBlur={formik.handleBlur}
                                                                >
                                                                    <option value="row">Row</option>
                                                                    <option value="column">Column</option>
                                                                </select>
                                                                {formik.errors.values?.[index]?.view_type && formik.touched.values?.[index]?.view_type && (
                                                                    <span className="text-danger small d-flex text-left">
                                                                        {formik.errors.values[index].view_type}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Image Upload */}
                                                            <div className="col-lg-4">
                                                                <label className="small text-muted mb-1 d-block text-left">Image (Optional)</label>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <input
                                                                        type="file"
                                                                        accept="image/*"
                                                                        className="form-control"
                                                                        onChange={(e) => {
                                                                            if (e.target.files && e.target.files[0]) {
                                                                                const file = e.target.files[0];
                                                                                formik.setFieldValue(`values.${index}.image`, file);
                                                                                const reader = new FileReader();
                                                                                reader.onload = (ev) => {
                                                                                    formik.setFieldValue(`values.${index}.preview`, ev.target.result);
                                                                                };
                                                                                reader.readAsDataURL(file);
                                                                            }
                                                                        }}
                                                                    />
                                                                    {val.preview && (
                                                                        <img
                                                                            src={val.preview}
                                                                            alt="preview"
                                                                            style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "4px", border: "1px solid #ccc" }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Action Button */}
                                                            <div className="col-lg-1 text-end">
                                                                {index === 0 ? (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-success btn-sm mt-3"
                                                                        onClick={() => arrayHelpers.push({ ...defaultValItem })}
                                                                    >
                                                                        <i className="fa fa-plus"></i>
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-danger btn-sm mt-3"
                                                                        onClick={() => arrayHelpers.remove(index)}
                                                                    >
                                                                        <i className="fa fa-minus"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        />
                                    </div>

                                    <div className="col-md-12 mt-3">
                                        <button
                                            className="btn btn-main-primary signbtn mr-2"
                                            type="submit"
                                            disabled={submitted}
                                        >
                                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                            {t("btn_submit")}
                                        </button>
                                        <button
                                            className="btn ripple btn-secondary"
                                            type="button"
                                            onClick={() => navigate(-1)}
                                        >
                                            <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                            {t("btn_cancel")}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </FormikProvider>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddAttributeValue;
